import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Search, Download, Loader2, Package, ExternalLink, CheckCircle2 } from 'lucide-react';

const api = (window as any).api;

interface ModrinthHit {
  project_id: string;
  slug: string;
  title: string;
  description: string;
  icon_url?: string;
  downloads: number;
  categories: string[];
  versions: string[];
  latest_version?: string;
  author: string;
}

interface ModrinthVersion {
  id: string;
  name: string;
  version_number: string;
  game_versions: string[];
  loaders: string[];
  date_published: string;
  downloads: number;
  files: { filename: string; url: string; primary: boolean; size: number }[];
}

interface Props {
  profileId: string;
  mcVersion: string;
  loader: string; // 'vanilla' | 'forge' | 'fabric' | 'quilt'
  installedVersionIds: string[];
  onClose: () => void;
  onModInstalled: (mod: any) => void;
}

const ModrinthSearchModal: React.FC<Props> = ({
  profileId,
  mcVersion,
  loader,
  installedVersionIds,
  onClose,
  onModInstalled,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ModrinthHit[]>([]);
  const [totalHits, setTotalHits] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedProject, setSelectedProject] = useState<ModrinthHit | null>(null);
  const [versions, setVersions] = useState<ModrinthVersion[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [installingId, setInstallingId] = useState<string | null>(null);
  const [justInstalled, setJustInstalled] = useState<string[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);
  const LIMIT = 20;

  useEffect(() => { inputRef.current?.focus(); }, []);

  const loaderFilter = loader !== 'vanilla' ? loader : undefined;

  const search = useCallback(async (q: string, off = 0) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.modrinthSearch({
        query: q,
        projectType: 'mod',
        loaders: loaderFilter,
        gameVersions: mcVersion,
        limit: LIMIT,
        offset: off,
      });
      if (!res.success) throw new Error(res.error || 'Erreur de recherche');
      if (off === 0) setResults(res.hits || []);
      else setResults(prev => [...prev, ...(res.hits || [])]);
      setTotalHits(res.total_hits || 0);
      setOffset(off);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [loaderFilter, mcVersion]);

  // Initial load: show popular mods
  useEffect(() => { search(''); }, [search]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    search(query);
  };

  const openProject = async (hit: ModrinthHit) => {
    setSelectedProject(hit);
    setVersions([]);
    setLoadingVersions(true);
    try {
      const res = await api.modrinthGetVersions({
        projectId: hit.project_id,
        loaders: loaderFilter,
        gameVersions: mcVersion,
      });
      if (!res.success) throw new Error(res.error);
      setVersions(res.versions || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingVersions(false);
    }
  };

  const installVersion = async (versionId: string) => {
    setInstallingId(versionId);
    setError(null);
    try {
      const res = await api.modrinthInstallMod({ profileId, versionId });
      if (!res.success) throw new Error(res.error || 'Échec de l\'installation');
      setJustInstalled(prev => [...prev, versionId]);
      onModInstalled(res.mod);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setInstallingId(null);
    }
  };

  const formatDownloads = (n: number) =>
    n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(0)}k` : String(n);

  const formatSize = (bytes: number) =>
    bytes >= 1_000_000 ? `${(bytes / 1_000_000).toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-bg-primary border border-border-color rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border-color">
          {selectedProject ? (
            <button onClick={() => setSelectedProject(null)} className="p-1 rounded hover:bg-bg-secondary text-text-muted hover:text-text-primary transition-colors">
              <X className="w-4 h-4 rotate-45" style={{ transform: 'rotate(45deg)' }} />
            </button>
          ) : null}
          <div className="w-6 h-6 flex items-center justify-center">
            <img src="https://cdn.modrinth.com/landing-new/modrinth-text-logo.webp" alt="Modrinth" className="h-4 object-contain" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          </div>
          <h2 className="text-sm font-semibold text-text-primary flex-1">
            {selectedProject ? selectedProject.title : 'Ajouter un mod depuis Modrinth'}
          </h2>
          <span className="text-xs text-text-muted bg-bg-secondary px-2 py-0.5 rounded-full">{mcVersion} · {loader}</span>
          <button onClick={onClose} className="p-1 rounded hover:bg-bg-secondary text-text-muted hover:text-text-primary transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search bar (only on list view) */}
        {!selectedProject && (
          <form onSubmit={handleSearch} className="flex items-center gap-2 px-5 py-3 border-b border-border-color">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted" />
              <input
                ref={inputRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Rechercher des mods..."
                className="w-full bg-bg-secondary border border-border-color rounded-lg pl-9 pr-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gaming-accent/50"
              />
            </div>
            <button type="submit" disabled={loading} className="px-4 py-2 bg-gaming-accent/10 border border-gaming-accent/30 text-gaming-accent text-xs rounded-lg hover:bg-gaming-accent/20 transition-colors disabled:opacity-50">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Rechercher'}
            </button>
          </form>
        )}

        {/* Error */}
        {error && (
          <div className="mx-5 mt-3 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400">{error}</div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Project version list */}
          {selectedProject ? (
            <div className="p-5">
              {/* Project header */}
              <div className="flex items-start gap-3 mb-5">
                {selectedProject.icon_url ? (
                  <img src={selectedProject.icon_url} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-gaming-accent/10 border border-gaming-accent/20 flex items-center justify-center flex-shrink-0">
                    <Package className="w-6 h-6 text-gaming-accent" />
                  </div>
                )}
                <div>
                  <h3 className="text-base font-semibold text-text-primary">{selectedProject.title}</h3>
                  <p className="text-xs text-text-muted mt-0.5">par {selectedProject.author}</p>
                  <p className="text-xs text-text-secondary mt-1 line-clamp-2">{selectedProject.description}</p>
                </div>
              </div>

              {/* Versions */}
              {loadingVersions ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-6 h-6 text-gaming-accent animate-spin" />
                </div>
              ) : versions.length === 0 ? (
                <div className="text-center py-10 text-sm text-text-muted">
                  Aucune version compatible avec {mcVersion} / {loader}
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-text-muted mb-3">{versions.length} version(s) compatible(s)</p>
                  {versions.map(v => {
                    const isInstalled = installedVersionIds.includes(v.id) || justInstalled.includes(v.id);
                    const isInstalling = installingId === v.id;
                    const primaryFile = v.files.find(f => f.primary) || v.files[0];
                    return (
                      <div key={v.id} className="flex items-center justify-between bg-bg-secondary border border-border-color rounded-lg px-4 py-3 hover:border-gaming-accent/30 transition-colors">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-text-primary truncate">{v.name}</span>
                            <span className="text-[10px] bg-bg-primary border border-border-color px-1.5 py-0.5 rounded text-text-muted">{v.version_number}</span>
                          </div>
                          <p className="text-[10px] text-text-muted mt-0.5">
                            {primaryFile?.filename} · {primaryFile ? formatSize(primaryFile.size) : ''} · {formatDownloads(v.downloads)} téléchargements
                          </p>
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            {v.game_versions.slice(0, 3).map(gv => (
                              <span key={gv} className="text-[9px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-1.5 py-0.5 rounded">{gv}</span>
                            ))}
                            {v.loaders.map(l => (
                              <span key={l} className="text-[9px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1.5 py-0.5 rounded capitalize">{l}</span>
                            ))}
                          </div>
                        </div>
                        <button
                          onClick={() => !isInstalled && !isInstalling && installVersion(v.id)}
                          disabled={isInstalled || isInstalling}
                          className={`ml-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex-shrink-0 ${
                            isInstalled
                              ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 cursor-default'
                              : 'bg-gaming-accent/10 border border-gaming-accent/30 text-gaming-accent hover:bg-gaming-accent/20 disabled:opacity-50'
                          }`}
                        >
                          {isInstalling ? (
                            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Installation...</>
                          ) : isInstalled ? (
                            <><CheckCircle2 className="w-3.5 h-3.5" /> Installé</>
                          ) : (
                            <><Download className="w-3.5 h-3.5" /> Installer</>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            /* Search results grid */
            <div className="p-5">
              {loading && results.length === 0 ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 text-gaming-accent animate-spin" />
                </div>
              ) : results.length === 0 && !loading ? (
                <div className="text-center py-16">
                  <Package className="w-10 h-10 text-text-muted mx-auto mb-3" />
                  <p className="text-sm text-text-muted">Aucun résultat</p>
                </div>
              ) : (
                <>
                  {totalHits > 0 && (
                    <p className="text-xs text-text-muted mb-3">{totalHits.toLocaleString()} résultats</p>
                  )}
                  <div className="space-y-2">
                    {results.map(hit => (
                      <button
                        key={hit.project_id}
                        onClick={() => openProject(hit)}
                        className="w-full flex items-start gap-3 bg-bg-secondary border border-border-color rounded-lg px-4 py-3 hover:border-gaming-accent/40 hover:bg-bg-secondary/80 transition-colors text-left"
                      >
                        {hit.icon_url ? (
                          <img src={hit.icon_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-gaming-accent/10 border border-gaming-accent/20 flex items-center justify-center flex-shrink-0">
                            <Package className="w-5 h-5 text-gaming-accent" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-text-primary">{hit.title}</span>
                            <span className="text-[10px] text-text-muted">par {hit.author}</span>
                          </div>
                          <p className="text-xs text-text-muted mt-0.5 line-clamp-1">{hit.description}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-[10px] text-text-muted flex items-center gap-1">
                              <Download className="w-2.5 h-2.5" /> {formatDownloads(hit.downloads)}
                            </span>
                            {hit.categories.slice(0, 3).map(c => (
                              <span key={c} className="text-[9px] bg-bg-primary border border-border-color px-1.5 py-0.5 rounded text-text-muted capitalize">{c}</span>
                            ))}
                          </div>
                        </div>
                        <ExternalLink className="w-3.5 h-3.5 text-text-muted flex-shrink-0 mt-1" />
                      </button>
                    ))}
                  </div>

                  {/* Load more */}
                  {results.length < totalHits && (
                    <div className="mt-4 flex justify-center">
                      <button
                        onClick={() => search(query, offset + LIMIT)}
                        disabled={loading}
                        className="px-4 py-2 text-xs text-text-muted hover:text-text-primary border border-border-color rounded-lg hover:border-gaming-accent/30 transition-colors disabled:opacity-50"
                      >
                        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `Charger plus (${totalHits - results.length} restants)`}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  , document.body);
};

export default ModrinthSearchModal;
